import { useState, useEffect, useRef } from 'react';

/**
 * 텍스트 타이핑 효과를 제공하는 훅
 * @param {string} text - 전체 텍스트
 * @param {number} speed - 타이핑 속도 (ms)
 * @param {boolean} shouldAnimate - 애니메이션 적용 여부
 * @param {Function} onComplete - 완료 시 콜백
 */
export const useTypingEffect = (text, speed = 20, shouldAnimate = true, onComplete) => {
    const [displayedText, setDisplayedText] = useState(shouldAnimate ? '' : text);
    const [isTyping, setIsTyping] = useState(shouldAnimate);
    const indexRef = useRef(0);

    useEffect(() => {
        // 텍스트가 없거나 애니메이션이 필요 없는 경우
        if (!text || !shouldAnimate) {
            setDisplayedText(text);
            setIsTyping(false);
            if (onComplete) onComplete();
            return;
        }

        // 이미 완료된 상태에서 텍스트가 변경된 경우 리셋
        if (text !== displayedText && !isTyping && indexRef.current === 0) {
            setIsTyping(true);
            setDisplayedText('');
        }
    }, [text, shouldAnimate]); // displayedText, isTyping 의존성 제거

    useEffect(() => {
        if (!isTyping || !shouldAnimate) return;

        // 만약 이미 다 출력했다면 종료
        if (indexRef.current >= text.length) {
            setIsTyping(false);
            if (onComplete) onComplete();
            return;
        }

        const intervalId = setInterval(() => {
            // 한 번에 여러 글자를 출력하여 속도감 조절 가능
            // 여기서는 1글자씩 하되 속도를 빠르게 설정하는 것을 권장
            indexRef.current += 1; // 1~3 사이 랜덤으로 하면 더 자연스러울 수 있음

            if (indexRef.current >= text.length) {
                setDisplayedText(text);
                setIsTyping(false);
                clearInterval(intervalId);
                if (onComplete) onComplete();
            } else {
                setDisplayedText(text.slice(0, indexRef.current));
            }
        }, speed);

        return () => clearInterval(intervalId);
    }, [text, speed, isTyping, shouldAnimate, onComplete]);

    return { displayedText, isTyping };
};
